import React from 'react';
import { Box, Container, Typography, Paper, Breadcrumbs, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { themeColors } from '../App';

const PrivacyPolicy = () => {
  return (
    <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          sx={{ 
            mb: 4,
            '& .MuiBreadcrumbs-separator': {
              color: themeColors.primary
            }
          }}
        >
          <Link 
            component={RouterLink} 
            to="/"
            sx={{ 
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { color: themeColors.primary }
            }}
          >
            Home
          </Link>
          <Typography color={themeColors.primary}>Privacy Policy</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Typography 
          variant="h2" 
          sx={{ 
            mb: 4,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1a472a 0%, #2e7d32 50%, #4caf50 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Privacy Policy
        </Typography>

        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.1)',
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, color: themeColors.primary }}>
            Last Updated: March 1, 2025
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.8 }}>
            At Vibhuti Industries Pvt. Ltd., we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or interact with our services.
          </Typography>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ mb: 2, color: themeColors.primary, fontWeight: 600 }}>
              1. Information We Collect
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.8 }}>
              We collect information that you provide directly to us, including:
            </Typography>
            <ul style={{ color: 'rgba(0,0,0,0.6)', lineHeight: 1.8 }}>
              <li>Name and contact information</li>
              <li>Company name and designation</li>
              <li>Email address and phone number</li>
              <li>Correspondence and communication records</li>
              <li>Technical specifications and requirements</li>
            </ul>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ mb: 2, color: themeColors.primary, fontWeight: 600 }}>
              2. How We Use Your Information
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.8 }}>
              We use the collected information for the following purposes:
            </Typography>
            <ul style={{ color: 'rgba(0,0,0,0.6)', lineHeight: 1.8 }}>
              <li>To provide and maintain our services</li>
              <li>To process and fulfill your requests and orders</li>
              <li>To communicate with you about our products and services</li>
              <li>To improve our website and user experience</li>
              <li>To comply with legal obligations</li>
            </ul>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ mb: 2, color: themeColors.primary, fontWeight: 600 }}>
              3. Information Sharing and Disclosure
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.8 }}>
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described below:
            </Typography>
            <ul style={{ color: 'rgba(0,0,0,0.6)', lineHeight: 1.8 }}>
              <li>With service providers who assist in our operations</li>
              <li>To comply with legal requirements</li>
              <li>To protect our rights and property</li>
              <li>In connection with a business transfer or merger</li>
            </ul>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ mb: 2, color: themeColors.primary, fontWeight: 600 }}>
              4. Data Security
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.8 }}>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ mb: 2, color: themeColors.primary, fontWeight: 600 }}>
              5. Your Rights
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.8 }}>
              You have the right to:
            </Typography>
            <ul style={{ color: 'rgba(0,0,0,0.6)', lineHeight: 1.8 }}>
              <li>Access your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your information</li>
              <li>Object to processing of your information</li>
              <li>Withdraw consent</li>
            </ul>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ mb: 2, color: themeColors.primary, fontWeight: 600 }}>
              6. Contact Us
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
              If you have any questions about this Privacy Policy, please contact us at:
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.8 }}>
              Vibhuti Industries (India) Pvt. Ltd.<br />
              <Link
                href="https://maps.google.com/?q=E-143+Sector-63+Noida+Uttar+Pradesh+201301"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': {
                    color: themeColors.primary,
                    textDecoration: 'underline'
                  }
                }}
              >
                E-143, Sector-63, Noida,<br />
                Gautam Buddha Nagar, Uttar Pradesh - 201301, India
              </Link><br />
              <Link
                href="mailto:office@thevibuti.com"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': {
                    color: themeColors.primary,
                    textDecoration: 'underline'
                  }
                }}
              >
                Email: office@thevibuti.com
              </Link><br />
              Phone: {' '}
              <Link
                href="tel:+919999922657"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': {
                    color: themeColors.primary,
                    textDecoration: 'underline'
                  }
                }}
              >
                +91-9999922657
              </Link>
              {' / '}
              <Link
                href="tel:+919667674397"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': {
                    color: themeColors.primary,
                    textDecoration: 'underline'
                  }
                }}
              >
                9667674397
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
